import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, Menu, X, ArrowRight, Gamepad2 } from "lucide-react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { useI18n, LANGS } from "../lib/i18n";
import { useEffect, useState } from "react";
import { api, fixAssetUrl } from "../lib/api";
import { CategoriesDropdown, MobileCategoryMenu } from "./CategoriesDropdown";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [menu, setMenu] = useState(false);
  const [s, setS] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isGaming = location.pathname.startsWith("/gaming");

  useEffect(() => { api.get("/site-settings", { params: { _: Date.now() } }).then((r) => setS(r.data)); }, []);
  const gamingLogo = s?.gaming_logo_url;
  const showGamingNav = !!s?.gaming_enabled;
  const gamingLabel = s?.gaming_nav_label || "Gaming";

  return (
    <>
      {s?.header_announcement && (
        <div className="bg-zinc-950 text-white text-center text-xs py-2 px-4 relative z-[51]" data-testid="header-announcement">
          {s.header_announcement}
        </div>
      )}
      <header className={`glass-nav relative z-50`} data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to={isGaming ? "/gaming" : "/"} className="flex items-center gap-2 group" data-testid="logo-link">
              {isGaming && gamingLogo ? (
                <img src={fixAssetUrl(gamingLogo)} alt="Gaming" className="h-14 md:h-16 max-w-[260px] object-contain" data-testid="navbar-gaming-logo"/>
              ) : s?.logo_url ? (
                <img src={fixAssetUrl(s.logo_url)} alt={s?.brand_name || "Logo"} className="h-14 md:h-16 max-w-[260px] object-contain" data-testid="navbar-logo-img"/>
              ) : (
                <>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                    <ArrowRight className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="font-display font-bold text-xl md:text-2xl tracking-tight">{s?.brand_name || "Správný Směr"}</div>
                </>
              )}
            </Link>

            <nav className="hidden md:flex items-center gap-7">
              <CategoriesDropdown label={t("nav_products")}/>
              <Link to="/faq" className="text-base font-medium text-zinc-700 hover:text-blue-600 transition-colors" data-testid="nav-faq">Časté dotazy</Link>
              <Link to="/blog" className="text-base font-medium text-zinc-700 hover:text-blue-600 transition-colors" data-testid="nav-blog">{t("nav_blog")}</Link>
              {showGamingNav && (
                <Link to="/gaming" className={`inline-flex items-center gap-1.5 text-base font-medium transition-colors ${isGaming ? "text-purple-700" : "text-zinc-700 hover:text-purple-700"}`} data-testid="nav-gaming">
                  <Gamepad2 className="w-4 h-4"/> {gamingLabel}
                </Link>
              )}
            </nav>

            <div className="flex items-center gap-2">
              <div className="hidden sm:block relative" data-testid="lang-toggle">
                <details className="group">
                  <summary className="flex items-center gap-1.5 cursor-pointer list-none rounded-full bg-zinc-100 px-3 py-1.5 hover:bg-zinc-200 transition-colors">
                    <span className="text-base leading-none">{LANGS.find((l) => l.code === lang)?.flag}</span>
                    <span className="text-xs font-semibold uppercase">{lang}</span>
                    <svg className="w-3 h-3 text-zinc-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.4a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
                  </summary>
                  <div className="absolute right-0 mt-2 w-44 rounded-2xl border border-zinc-200 bg-white shadow-xl py-1.5 z-50">
                    {LANGS.map((l) => (
                      <button key={l.code} onClick={(e) => {
                        setLang(l.code);
                        e.target.closest("details")?.removeAttribute("open");
                        // Force a full reload so all dynamic content (products, blogs, CMS pages) re-fetches in the new language and currency
                        setTimeout(() => { window.location.reload(); }, 50);
                      }} className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${l.code === lang ? "font-semibold text-blue-600" : "text-zinc-700"}`} data-testid={`lang-${l.code}`}>
                        <span className="text-base">{l.flag}</span>
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                </details>
              </div>
              {user ? (
                <div className="flex items-center gap-1">
                  {user.role === "admin" && (
                    <Link to="/admin" className="text-xs font-semibold px-3 py-2 rounded-full text-orange-600 hover:bg-orange-50" data-testid="nav-admin">Admin</Link>
                  )}
                  <Link to="/ucet" className="p-2 rounded-full hover:bg-zinc-100 transition-colors" data-testid="nav-account"><User className="w-5 h-5 text-zinc-700"/></Link>
                  <button onClick={() => { logout(); navigate("/"); }} className="text-xs text-zinc-500 hover:text-zinc-900 px-2" data-testid="nav-logout">{t("nav_logout")}</button>
                </div>
              ) : (
                <Link to="/prihlaseni" className="hidden sm:inline-flex text-sm font-medium px-3 py-2 rounded-full hover:bg-zinc-100 transition-colors" data-testid="nav-login">{t("nav_login")}</Link>
              )}
              <button onClick={() => setOpen(true)} className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors" data-testid="cart-button">
                <ShoppingCart className="w-5 h-5 text-zinc-700"/>
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>
                )}
              </button>
              <button className="md:hidden p-2" onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle">
                {menu ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
              </button>
            </div>
          </div>
        </div>
        {menu && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <div className="px-4 py-3 flex flex-col gap-2">
              <Link to="/produkty" onClick={() => setMenu(false)} className="py-2 text-sm font-medium">{t("nav_products")}</Link>
              <MobileCategoryMenu onNavigate={() => setMenu(false)}/>
              <Link to="/faq" onClick={() => setMenu(false)} className="py-2 text-sm font-medium">Časté dotazy</Link>
              <Link to="/blog" onClick={() => setMenu(false)} className="py-2 text-sm font-medium">{t("nav_blog")}</Link>
              {showGamingNav && <Link to="/gaming" onClick={() => setMenu(false)} className="py-2 text-sm font-medium inline-flex items-center gap-1.5"><Gamepad2 className="w-4 h-4"/> {gamingLabel}</Link>}
              {!user && <Link to="/prihlaseni" onClick={() => setMenu(false)} className="py-2 text-sm font-medium">{t("nav_login")}</Link>}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
